/* ═══════════════════════════════════════════════════════════
   Brändi.

   Värit ja tunnisteet luetaan kannasta ja asetetaan CSS-muuttujiksi
   ennen ensimmäistä piirtoa. Yksikään väri ei ole koodissa kovana —
   se on koko white-label-lupauksen tekninen sisältö.

   Haku on julkinen: kirjautumissivu tarvitsee brändin, ja se on
   määritelmän mukaan se sivu jolla ei olla kirjautuneena.
   ═══════════════════════════════════════════════════════════ */

export interface Brandi {
  nimi: string;
  /** Instanssin kieli (004 `brandi.kieli`): käyttöliittymän oletus kun tunnuksella ei ole omaa (T439). */
  kieli?: string;
  /** Tässä instanssissa valittavat käyttöliittymän kielet (palvelimen KIELET, liettua 11.10.2026). */
  kielet?: string[];
  tuotenimi: string;
  /** Lyhyt tekstitunnus ylapalkkiin, kun logotiedostoa ei ole. */
  tunnus: string;
  tunnus_ala: string | null;
  /**
   * Pysyvä merkintä tunnuksen perään: SAMPO · DEMO.
   *
   * Kertoo katsojalle mitä instanssia hän katsoo. Demossa aineisto on
   * keksittyä, ja uskottava keksitty nimi osuu määritelmän mukaan
   * lähelle olemassa olevaa — merkintä tekee siitä rehellistä ilman
   * että nimien tarvitsee olla huonoja. null = ei merkintää.
   */
  merkinta?: string | null;
  /** 208: merkinnän englanninkielinen pari; null = englanniksikin näytetään `merkinta`. */
  merkinta_en?: string | null;
  logo: string | null;
  /** Vaalea versio tummaa ylapalkkia varten. null = käytetään samaa. */
  logo_tumma?: string | null;
  /** Onko ylapalkki tumma. Ratkaisee kumpi logo valitaan. */
  ylapalkki_tumma?: boolean;
  /**
   * Välilehden kuvake. Asetettu migraatiossa 004, mutta mikään ei
   * lukenut sitä ennen 19.8.2026 — arvo oli kannassa ja vaikutus
   * nolla.
   */
  favicon?: string | null;
  /** Brändin aksenttiväri (Modernist: ainoa asiakaskohtainen väri). */
  korostus?: string;
  /** Yrityksen tiedot tarjousdokumenttiin. */
  ytunnus?: string;
  osoite?: string;
  puhelin?: string;
  verkkosivu?: string;
  tukiosoite?: string;
  varit: Record<string, string>;
}

/**
 * Oletus, jota käytetään jos haku epäonnistuu.
 *
 * Sovellus ei saa jäädä lataamatta siksi, että brändipalvelu on
 * hetkellisesti alhaalla — ruudun pitää olla luettava vaikka väärillä
 * väreillä. Sama arvo on CSS-tiedostossa, joten tämä on varmistus
 * eikä ainoa lähde.
 */
// Nimisääntö (Toni 8.10.2026): asiakkaalle näkyvä nimi on «Sampo Industrial», ei pelkkä «Sampo».
const OLETUS: Brandi = {
  nimi: "Sampo Industrial",
  tuotenimi: "Sampo Industrial",
  tunnus: "SAMPO",
  tunnus_ala: null,
  logo: null,
  varit: {},
};

/**
 * Asettaa brändin AKSENTIN :root-tasolle (Modernist).
 *
 * Vain aksentti tulee brändistä — neutraalit (tausta, teksti, viivat)
 * ovat kiinteät Modernist-arvot tyylit.css:ssä. Näin asiakkaan vanhat
 * harmaasävyt eivät ylikirjoita uutta palettia, ja aksenttirampit
 * (--korostus-600/700/…) johdetaan aksentista CSS:ssä. Kanoninen lähde
 * on `brandi.korostus`; varit.korostus on varalla vanhaa dataa varten.
 */
function asetaAksentti(...ehdokkaat: (string | undefined)[]) {
  const arvo = ehdokkaat.find(
    (a) => typeof a === "string" && /^#[0-9a-fA-F]{3,8}$/.test(a)
  );
  if (arvo) document.documentElement.style.setProperty("--korostus", arvo);
}

/**
 * Varoitusväri instanssikohtaisesti (Toni 22.10.2026 klo 12.40): `varit.varoitus` korvaa tyylit.css:n oletuksen --varoitus.
 * Asiakkaan korostusväri voi olla sama sävy kuin oletusvaroitus (pekilossa JSG:n oranssi), jolloin varoitus ei erottuisi —
 * siksi arvo valitaan instanssin brändin mukaan eikä koodissa. Varoitusnappi erottuu lisäksi muodolla, ei pelkällä värillä.
 */
function asetaVaroitus(arvo: string | undefined) {
  if (typeof arvo === "string" && /^#[0-9a-fA-F]{3,8}$/.test(arvo)) document.documentElement.style.setProperty("--varoitus", arvo);
}

/**
 * Välilehden kuvake brändistä.
 *
 * `brandi.favicon` on ollut konfiguraatiossa migraatiosta 004 asti
 * eikä mikään ole lukenut sitä — sama muoto kuin havainnointi joka
 * kirjoitti puuterivejä joita yksikään näkymä ei lukenut. Nyt se
 * luetaan.
 *
 * Jos arvo on 004:n paikanpitäjä (`/config/brandi/favicon.png`),
 * tiedostoa ei yleensä ole, ja sen käyttö tuottaisi 404:n joka
 * sivulatauksella. Silloin kuvake PIIRRETÄÄN aksenttivärillä ja
 * tunnuksen alkukirjaimista: jokainen instanssi saa oman
 * tunnistettavan välilehden ilman että kenenkään tarvitsee
 * toimittaa tiedostoa.
 */
const OLETUSPOLKU = "/config/brandi/favicon.png";

function piirraKuvake(tunnus: string, vari: string): string {
  const kirjaimet = (tunnus || "S").slice(0, 2).toUpperCase();
  const koko = kirjaimet.length > 1 ? 26 : 34;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" fill="${vari}"/>` +
    `<text x="32" y="32" fill="#fff" font-family="Arial,Helvetica,sans-serif" ` +
    `font-size="${koko}" font-weight="700" text-anchor="middle" ` +
    `dominant-baseline="central">${kirjaimet}</text></svg>`;
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

function asetaKuvake(b: Brandi): void {
  const oma = b.favicon && b.favicon !== OLETUSPOLKU ? b.favicon : null;
  const vari = (b.varit.korostus || b.korostus || "#17191B").trim();
  const href = oma ?? piirraKuvake(b.tunnus || b.nimi || "S", vari);

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = oma ? "" : "image/svg+xml";
  link.href = href;
}

let valimuisti: Brandi | null = null;

export async function haeBrandi(): Promise<Brandi> {
  if (valimuisti) return valimuisti;
  try {
    const vastaus = await fetch("/api/brandi", { credentials: "same-origin" });
    if (!vastaus.ok) throw new Error(String(vastaus.status));
    const data = (await vastaus.json()) as Partial<Brandi>;
    const b: Brandi = { ...OLETUS, ...data, varit: data.varit ?? {} };
    asetaAksentti(b.korostus, b.varit.korostus);
    asetaVaroitus(b.varit.varoitus);
    if (b.nimi) {
      const perus = b.tuotenimi || b.nimi;
      // Merkintä kuuluu myös välilehteen: demo tunnistetaan usein
      // avoimien välilehtien joukosta eikä ruudulta.
      document.title = b.merkinta ? perus + " · " + b.merkinta : perus;
    }
    asetaKuvake(b);
    valimuisti = b;
    return b;
  } catch {
    // Hiljainen paluu oletukseen on tässä oikein: vaihtoehto olisi
    // näyttää virhesivu siksi, että värit eivät latautuneet.
    return OLETUS;
  }
}
